import 'react-toastify/dist/ReactToastify.css';

import { parseUrl } from 'modules/common';
import { useRouter } from 'next/router';
import { ReactNode, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';

import { UserType } from '../../types/UserTypes';
import ErrorBoundary from '../errorBoundary/ErrorBoundary';
import Footer from '../footer/Footer';
import Header from '../header/Header';
import {
  Content,
  GlobalStyle,
  Main,
  TipStyled,
  ToastContainerStyled,
  Wrapper,
} from './Layout.styles';

const messages = {
  acceptTermsSuccess:
    'You have successfully accepted the bid2board terms & conditions!',
  addFavoriteSuppliers:
    'You added {{supplierName}} to favorite suppliers successfully!',
  removeFavoriteSuppliers:
    'You removed {{supplierName}} from favorite suppliers successfully!',
  addUnfavoredSuppliers:
    'You added {{supplierName}} to unfavored suppliers successfully!',
  removeUnfavoredSuppliers:
    'You removed {{supplierName}} from unfavored suppliers successfully!',
  addOfferSuccess: 'Your offer was successfully submitted!',
  addRatingSuccess:
    'You have successfully rated the supplier: {{supplierName}}',
  addShipSuccess: 'Your ship added successfully!',
  addTransactionSuccess: 'The transaction was successfully added!',
  acceptOfferSuccess: 'You have accepted the offer successfully!',
  buyBundleSuccess: `You successfully bought a bundle of {{bundle}}. We will sent you the invoice shortly.`,
  cancelServiceSuccess: 'The service is canceled successfully!',
  cancelSubscriptionSuccess:
    'You successfully requested the cancelation of your subscription.',
  certificationApproved: 'The certification was successfully approved',
  certificationRejected: 'The certification was rejected',
  changeStatusToInProgressSupplierTroubleshootingSuccess:
    'You have successfully changed the status of this troubleshooting to working on it!',
  changeStatusToInProgressShippingCompanyTroubleshootingSuccess:
    'You have successfully changed the status of this troubleshooting to waiting response!',
  changePasswordSuccess: 'Your password changed successfully!',
  changeOfferTypeToManHours: 'Your offer changed to man hours successfully!',
  confirmOfferSuccess:
    'You successfully confirmed the offer. Please wait for the supplier to also confirm the service!',
  completeServiceSuccess: 'The service is completed successfully!',
  completeTroubleshootingSuccess:
    'The troubleshooting is completed successfully!',
  contactUsSuccess:
    'Your message was sent successfully! We will respond to you asap!',
  createServiceRequestSuccess: 'Your service request created successfully!',
  createTroubleshootingRequestSuccess:
    'Your troubleshooting request created successfully!',
  deleteOfferSuccess: 'Your offer has been deleted sucessfully!',
  deleteShipSuccess: 'Your ship has been deleted sucessfully!',
  duplicateShipSuccess: 'Your ship was copied successfully!',
  editProfileSuccess: 'Your profile was updated successfully!',
  finalizeOfferSuccess: 'You have confirmed the service successfully!',
  passwordResetError:
    'Something went wrong please try to reset your password again',
  registerSuccess: 'You have registered successfully. Please login!',
  sendReminderSuccess:
    'You have send a reminder to {{supplierName}} successfully!',
  sessionExpired: 'Your session has expired, please login again!',
  supplierWithdrawnOfferSuccess:
    'You have withdrawn from this service request.',
  updateUserSuccess: 'You have updated the user successfully!',
  updateUserPreferencesSuccess: 'Your preferences was saved successfully!',
  updateRatingSuccess:
    'You have successfully update your rating for the supplier: {{supplierName}}',
  updateOfferSuccess: 'Your offer was updated successfully!',
  updateShipSuccess: 'Your ship was updated successfully!',
  updateServiceRequestSuccess: 'The service request was updated successfully',
  updateTransactionSuccess: 'The transaction was successfully updated',
  updateTroubleshootingRequestSuccess:
    'The troubleshooting request was successfully updated!',
  userAdded: 'The new user was added successfully!',
  userApproved: 'The supplier was successfully approved',
  userRejected: 'The supplier was rejected',
  withdrawOfferSuccess: 'You canceled the acceptance of the offer!',
};

// layout component props type
interface LayoutProps {
  children: ReactNode;
  isLoading: boolean;
  user: UserType;
  noExtraSpaceFooter?: boolean;
}

const Layout = ({
  children,
  isLoading,
  user,
  noExtraSpaceFooter = false,
}: LayoutProps): JSX.Element => {
  const [isTooltipVisible, setTooltipVisibility] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false);
  const router = useRouter();
  useEffect(() => {
    setTooltipVisibility(true);
  }, []);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  useEffect(() => {
    const { urlParams } = parseUrl();

    if (urlParams?.message) {
      const message = Object.entries(urlParams)
        .reduce((acc, [key, value]) => {
          return acc?.replaceAll(`{{${key}}}`, value);
        }, messages[urlParams.message])
        ?.replaceAll('%20', ' ');

      toast[urlParams?.type || 'success'](message);

      // remove message from url
      setTimeout(() => {
        const { hash } = parseUrl();

        window.history.pushState(
          null,
          null,
          `${window.location.pathname}${hash ? `#${hash}` : ''}`
        );
      }, 5000);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line dot-notation
    const isIE = false || !!document['documentMode'];
    if (isIE) {
      router.push('/not-supported');
    }
  }, []);

  useEffect(() => {
    const toggleScrollToTop = () => {
      const scrollLimit = 1000;
      const scrollValue = window.scrollY;
      if (scrollValue >= scrollLimit) {
        setShowScrollToTop(true);
      } else {
        setShowScrollToTop(false);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', toggleScrollToTop);
    }
    return () => window.removeEventListener('scroll', toggleScrollToTop);
  }, []);

  return (
    <ErrorBoundary>
      <Wrapper>
        {!isLoading ? (
          <>
            <Content>
              <Header user={user} />
              <Main>{children}</Main>
              <GlobalStyle />
              {isTooltipVisible && <ReactTooltip effect="solid" multiline />}
            </Content>
            {showScrollToTop && (
              <TipStyled
                shadow
                icon="upArrow"
                size="lg"
                tooltip="Scroll to the top"
                // onClick scroll to top of the page
                onClick={() => window.scrollTo(0, 0)}
              />
            )}
            <Footer noExtraSpace={noExtraSpaceFooter} />
          </>
        ) : (
          <p>Loading...</p>
        )}
        <ToastContainerStyled
          position="top-center"
          autoClose={10000}
          hideProgressBar
        />
      </Wrapper>
    </ErrorBoundary>
  );
};

export default Layout;
